import React, { useEffect, useState } from "react";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { isAdminRole, accessSummary, ROLE_LABEL } from "../lib/access";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Switch } from "../components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import { UserPlus, Plus, ShieldCheck, Clock, Pencil, CalendarClock, Trash2, AlertTriangle, History, Mail } from "lucide-react";
import { toast } from "sonner";
import { tzBanner, nowZonedWall, zonedWallToUtcIso, fmtDateTime } from "../lib/timezone";

const ROLE_OPTIONS = [
  { value: "employee", label: "Employee" },
  { value: "supervisor", label: "Supervisor" },
  { value: "admin", label: "Admin" },
];

function RoleBadge({ role }) {
  if (role === "admin" || role === "system_admin")
    return <Badge className="gap-1 bg-blue-600 text-white"><ShieldCheck className="h-3 w-3" /> {ROLE_LABEL[role]}</Badge>;
  if (role === "supervisor")
    return <Badge className="bg-amber-500 text-white">Supervisor</Badge>;
  return <Badge variant="outline" className="text-slate-600">Employee</Badge>;
}

function EditMemberDialog({ member, divisions, shifts, tz, onSaved }) {
  const [open, setOpen] = useState(false);
  const activeDivs = divisions.filter((d) => d.active !== false).map((d) => d.name);
  const [name, setName] = useState(member.name);
  const [role, setRole] = useState(member.role);
  const [primary, setPrimary] = useState(member.primary_division || "");
  const [authorized, setAuthorized] = useState(member.authorized_divisions || []);
  const [supervised, setSupervised] = useState(member.supervised_divisions || []);
  const [viewAll, setViewAll] = useState(!!member.view_all_divisions);
  const [active, setActive] = useState(member.active !== false);
  const [canManageSchedule, setCanManageSchedule] = useState(!!member.can_manage_schedule);
  const [defaultScheduleRole, setDefaultScheduleRole] = useState(member.default_schedule_role || "");
  const [scheduleRoles, setScheduleRoles] = useState([]);
  // shift change
  const [shift, setShift] = useState(member.shift_name || "");
  const [effective, setEffective] = useState("immediate");
  const [effectiveAt, setEffectiveAt] = useState(nowZonedWall(tz));
  const [conflict, setConflict] = useState("keep");
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activity, setActivity] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const reset = () => {
    setName(member.name);
    setRole(member.role);
    setPrimary(member.primary_division || "");
    setAuthorized(member.authorized_divisions || []);
    setSupervised(member.supervised_divisions || []);
    setViewAll(!!member.view_all_divisions);
    setActive(member.active !== false);
    setCanManageSchedule(!!member.can_manage_schedule);
    setDefaultScheduleRole(member.default_schedule_role || "");
    setShift(member.shift_name || "");
    setEffective("immediate");
    setEffectiveAt(nowZonedWall(tz));
    setConflict("keep");
    setPreview(null);
    setActivity(null);
    setConfirmDelete(false);
    setDeleting(false);
    api.get(`/team/${member.id}/shift-preview`).then((r) => setPreview(r.data)).catch(() => {});
    api.get(`/team/${member.id}/activity`).then((r) => setActivity(r.data)).catch(() => {});
    api.get("/schedule-roles?active_only=true").then((r) => setScheduleRoles(r.data)).catch(() => {});
  };

  const deleteMember = async () => {
    setDeleting(true);
    try {
      await api.delete(`/team/${member.id}`);
      toast.success(`${member.name} permanently deleted`);
      setOpen(false);
      onSaved();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setDeleting(false); }
  };

  const [resending, setResending] = useState(false);
  const resend = async () => {
    setResending(true);
    try {
      await api.post(`/team/${member.id}/resend-onboarding`);
      toast.success(`Invite email resent to ${member.email}`);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setResending(false); }
  };

  const toggle = (list, setList, nm) =>
    setList(list.includes(nm) ? list.filter((n) => n !== nm) : [...list, nm]);

  const shiftChanged = shift && shift !== (member.shift_name || "");
  const hasConflicts = shiftChanged && (preview?.future_assignment_count || 0) > 0;

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/team/${member.id}/access`, {
        name,
        role,
        primary_division: primary || null,
        authorized_divisions: authorized,
        supervised_divisions: role === "supervisor" ? supervised : [],
        view_all_divisions: role === "admin" ? viewAll : false,
        active,
        can_manage_schedule: role === "supervisor" ? canManageSchedule : false,
        default_schedule_role: defaultScheduleRole,
      });
      if (shiftChanged) {
        await api.post(`/team/${member.id}/shift`, {
          shift_name: shift,
          effective,
          effective_at: effective === "scheduled" ? zonedWallToUtcIso(effectiveAt, tz) : null,
          future_assignments: hasConflicts ? conflict : "keep",
        });
      }
      toast.success("Member updated");
      setOpen(false);
      onSaved();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const labelCls = "text-xs font-bold uppercase tracking-wider text-slate-500";

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) reset(); }}>
      <DialogTrigger asChild>
        <Button data-testid={`edit-member-open-${member.id}`} variant="outline" size="sm" className="h-8 gap-1 text-blue-700 border-blue-200 hover:bg-blue-50">
          <Pencil className="h-3.5 w-3.5" /> Edit Member
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DialogTitle>Edit Member · {member.name}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          {member.onboarding_pending && (
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-amber-200 bg-amber-50 p-3" data-testid={`edit-pending-notice-${member.id}`}>
              <p className="text-xs text-amber-800">This member hasn't set up their account yet. Resend the secure email link so they can set their password.</p>
              <Button
                data-testid={`edit-resend-invite-${member.id}`}
                variant="outline"
                size="sm"
                disabled={resending}
                onClick={resend}
                className="h-8 gap-1 shrink-0 text-amber-700 border-amber-300 hover:bg-amber-100"
              >
                <Mail className="h-3.5 w-3.5" /> {resending ? "Sending…" : "Resend invite"}
              </Button>
            </div>
          )}
          <div>
            <Label className={labelCls}>Full name</Label>
            <Input data-testid={`edit-name-${member.id}`} value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className={labelCls}>Role</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger data-testid={`edit-role-${member.id}`} className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className={labelCls}>Primary division</Label>
              <Select value={primary} onValueChange={setPrimary}>
                <SelectTrigger data-testid={`edit-primary-${member.id}`} className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{activeDivs.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          {/* Assigned shift + change flow */}
          <div className="rounded-md border border-slate-200 p-3">
            <Label className={labelCls}>Assigned shift</Label>
            <Select value={shift} onValueChange={setShift}>
              <SelectTrigger data-testid={`edit-shift-${member.id}`} className="mt-1.5"><SelectValue placeholder="Assign shift" /></SelectTrigger>
              <SelectContent>{shifts.map((s) => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
            {preview?.pending_shift && (
              <p className="mt-2 flex items-center gap-1 text-xs text-amber-700"><CalendarClock className="h-3 w-3" /> Scheduled change to {preview.pending_shift} on {fmtDateTime(preview.pending_shift_effective_at, tz)}</p>
            )}
            <div className="mt-3 border-t border-slate-100 pt-3">
              <Label className={labelCls}>Default schedule role</Label>
              <Select value={defaultScheduleRole || "__none__"} onValueChange={(v) => setDefaultScheduleRole(v === "__none__" ? "" : v)}>
                <SelectTrigger data-testid={`edit-default-role-${member.id}`} className="mt-1.5"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None</SelectItem>
                  {scheduleRoles.map((r) => <SelectItem key={r.id} value={r.name}>{r.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="mt-1 text-xs text-slate-400">Preselected when this employee is added to the schedule (still editable per assignment). This is an operational role, separate from the account role above.</p>
            </div>
            {shiftChanged && (
              <div className="mt-3 space-y-3 border-t border-slate-100 pt-3" data-testid={`shift-change-panel-${member.id}`}>
                <div>
                  <Label className={labelCls}>Effective</Label>
                  <Select value={effective} onValueChange={setEffective}>
                    <SelectTrigger data-testid={`edit-effective-${member.id}`} className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="immediate">Immediately</SelectItem>
                      <SelectItem value="scheduled">On a selected date &amp; time</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {effective === "scheduled" && (
                  <div>
                    <Input data-testid={`edit-effective-at-${member.id}`} type="datetime-local" value={effectiveAt} onChange={(e) => setEffectiveAt(e.target.value)} />
                    <p className="mt-1 text-xs text-slate-400">{tzBanner(tz)}</p>
                  </div>
                )}
                {hasConflicts && (
                  <div className="rounded-md border border-amber-200 bg-amber-50 p-2.5">
                    <p className="flex items-start gap-1 text-xs text-amber-800">
                      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" />
                      {preview.future_assignment_count} future schedule assignment(s) still use {member.shift_name}. Choose what to do:
                    </p>
                    <Select value={conflict} onValueChange={setConflict}>
                      <SelectTrigger data-testid={`edit-conflict-${member.id}`} className="mt-2 bg-white"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="keep">Keep existing scheduled assignments</SelectItem>
                        <SelectItem value="move">Move future assignments to {shift}</SelectItem>
                        <SelectItem value="review">Review assignments individually (leave as-is)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            )}
          </div>

          {role === "admin" && (
            <div className="flex items-center justify-between rounded-md border border-slate-200 p-3">
              <div>
                <p className="text-sm font-semibold text-slate-800">View all divisions</p>
                <p className="text-xs text-slate-500">Let this admin view records across every division.</p>
              </div>
              <Switch data-testid={`edit-viewall-${member.id}`} checked={viewAll} onCheckedChange={setViewAll} />
            </div>
          )}

          <div>
            <Label className={labelCls}>Authorized divisions</Label>
            <div className="mt-2 grid grid-cols-2 gap-2">
              {activeDivs.map((n) => (
                <label key={n} className="flex items-center gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-700">
                  <Checkbox data-testid={`edit-auth-${member.id}-${n}`} checked={authorized.includes(n)} onCheckedChange={() => toggle(authorized, setAuthorized, n)} />
                  {n}
                </label>
              ))}
            </div>
          </div>

          {role === "supervisor" && (
            <>
              <div>
                <Label className={labelCls}>Divisions supervised</Label>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {activeDivs.map((n) => (
                    <label key={n} className="flex items-center gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-700">
                      <Checkbox data-testid={`edit-superv-${member.id}-${n}`} checked={supervised.includes(n)} onCheckedChange={() => toggle(supervised, setSupervised, n)} />
                      {n}
                    </label>
                  ))}
                </div>
              </div>
              <div className="flex items-center justify-between rounded-md border border-slate-200 p-3">
                <div>
                  <p className="text-sm font-semibold text-slate-800">Manage schedule assignments</p>
                  <p className="text-xs text-slate-500">Allow this supervisor to change shifts for their supervised divisions.</p>
                </div>
                <Switch data-testid={`edit-manage-schedule-${member.id}`} checked={canManageSchedule} onCheckedChange={setCanManageSchedule} />
              </div>
            </>
          )}

          <div className="flex items-center justify-between rounded-md border border-slate-200 p-3">
            <div>
              <p className="text-sm font-semibold text-slate-800">Active</p>
              <p className="text-xs text-slate-500">Inactive members cannot log in or receive handoffs.</p>
            </div>
            <Switch data-testid={`edit-active-${member.id}`} checked={active} onCheckedChange={setActive} />
          </div>

          {(preview?.assignment_history || []).length > 0 && (
            <div className="rounded-md border border-slate-200 p-3">
              <p className={`${labelCls} flex items-center gap-1`}><History className="h-3 w-3" /> Assignment history</p>
              <div className="mt-2 space-y-1.5" data-testid={`assignment-history-${member.id}`}>
                {(preview.assignment_history || []).slice().reverse().map((h, i) => (
                  <div key={i} className="text-xs text-slate-600">
                    <span className="font-semibold text-slate-800">{h.previous_shift || "—"} → {h.new_shift}</span>
                    {h.scheduled ? " (scheduled)" : ""} · {fmtDateTime(h.effective_at, tz)}
                    <span className="text-slate-400"> · by {h.changed_by_name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Danger zone — permanent delete */}
          <div className="rounded-md border border-red-200 bg-red-50/40 p-3" data-testid={`danger-zone-${member.id}`}>
            <p className={`${labelCls} flex items-center gap-1 text-red-600`}><Trash2 className="h-3 w-3" /> Permanently delete</p>
            {!activity ? (
              <p className="mt-1.5 text-xs text-slate-400" data-testid={`delete-loading-${member.id}`}>Checking activity…</p>
            ) : activity.can_delete ? (
              <>
                <p className="mt-1.5 text-xs text-slate-600">This member has no historical records and can be safely removed. This cannot be undone.</p>
                {!confirmDelete ? (
                  <Button
                    data-testid={`delete-member-open-${member.id}`}
                    variant="outline"
                    size="sm"
                    onClick={() => setConfirmDelete(true)}
                    className="mt-2 h-8 gap-1 text-red-600 border-red-200 hover:bg-red-100"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Delete member
                  </Button>
                ) : (
                  <div className="mt-2 flex items-center gap-2">
                    <Button
                      data-testid={`delete-member-confirm-${member.id}`}
                      size="sm"
                      disabled={deleting}
                      onClick={deleteMember}
                      className="h-8 gap-1 bg-red-600 text-white hover:bg-red-700"
                    >
                      {deleting ? "Deleting…" : "Confirm delete"}
                    </Button>
                    <Button
                      data-testid={`delete-member-cancel-${member.id}`}
                      variant="outline"
                      size="sm"
                      disabled={deleting}
                      onClick={() => setConfirmDelete(false)}
                      className="h-8"
                    >
                      Cancel
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <p className="mt-1.5 flex items-start gap-1 text-xs text-slate-600" data-testid={`delete-blocked-${member.id}`}>
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" />
                Has historical records — deactivate instead. Members with handoffs, acknowledgements, comments, schedule history or feedback cannot be permanently deleted.
              </p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button data-testid={`edit-save-${member.id}`} disabled={saving} onClick={save} className="bg-blue-600 hover:bg-blue-700 text-white">{saving ? "Saving…" : "Save changes"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function TeamManagement() {
  const { user } = useAuth();
  const isAdmin = isAdminRole(user);
  const [team, setTeam] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [handoffs, setHandoffs] = useState([]);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [invite, setInvite] = useState({ name: "", email: "", role: "employee", shift_name: "", primary_division: "" });
  const [newShift, setNewShift] = useState("");
  const [divisions, setDivisions] = useState([]);
  const [newDivision, setNewDivision] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [divFilter, setDivFilter] = useState("all");

  const load = () => {
    if (isAdminRole(user)) {
      api.get("/team").then((r) => setTeam(r.data)).catch(() => {});
      api.get("/shifts").then((r) => setShifts(r.data)).catch(() => {});
      api.get("/divisions").then((r) => setDivisions(r.data)).catch(() => {});
    }
    api.get("/handoffs").then((r) => setHandoffs(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const activeDivNames = divisions.filter((d) => d.active !== false).map((d) => d.name);

  const resendInvite = async (m) => {
    try {
      await api.post(`/team/${m.id}/resend-onboarding`);
      toast.success(`Invite email resent to ${m.email}`);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const submitInvite = async () => {
    try {
      const payload = {
        ...invite,
        authorized_divisions: invite.primary_division ? [invite.primary_division] : [],
        supervised_divisions: invite.role === "supervisor" && invite.primary_division ? [invite.primary_division] : [],
      };
      await api.post("/team/invite", payload);
      toast.success(`Invite email sent to ${invite.email}`);
      setInviteOpen(false);
      setInvite({ name: "", email: "", role: "employee", shift_name: "", primary_division: "" });
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const addShift = async () => {
    if (!newShift.trim()) return;
    try {
      await api.post("/shifts", { name: newShift.trim() });
      setNewShift("");
      toast.success("Shift created");
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const renameShift = async (id, name) => {
    if (!name.trim()) return;
    try {
      await api.patch(`/shifts/${id}`, { name: name.trim() });
      toast.success("Shift renamed for all assigned employees");
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const deleteShift = async (id) => {
    try {
      await api.delete(`/shifts/${id}`);
      toast.success("Shift deleted");
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const addDivision = async () => {
    if (!newDivision.trim()) return;
    try {
      await api.post("/divisions", { name: newDivision.trim() });
      setNewDivision("");
      toast.success("Division added");
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const renameDivision = async (id, name) => {
    if (!name.trim()) return;
    try {
      await api.patch(`/divisions/${id}`, { name: name.trim() });
      toast.success("Division renamed");
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const toggleDivision = async (id, active) => {
    try {
      await api.patch(`/divisions/${id}`, { active });
      toast.success(active ? "Division activated" : "Division deactivated");
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const ackHistory = handoffs.flatMap((h) =>
    (h.acknowledgements || []).map((a) => ({ ...a, shift: h.shift_name, handoff_id: h.id }))
  ).sort((x, y) => new Date(y.created_at) - new Date(x.created_at));

  const labelCls = "text-xs font-bold uppercase tracking-wider text-slate-500";

  return (
    <div className="animate-fade-up space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">Administration</p>
          <h1 className="font-display text-3xl font-black tracking-tight text-slate-900">Team Management</h1>
        </div>
        {isAdmin && (
          <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
            <DialogTrigger asChild>
              <Button data-testid="invite-open-button" className="gap-2 bg-blue-600 hover:bg-blue-700 text-white">
                <UserPlus className="h-4 w-4" /> Add Member
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Add Team Member</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label className={labelCls}>Full name</Label>
                  <Input data-testid="invite-name" value={invite.name} onChange={(e) => setInvite({ ...invite, name: e.target.value })} className="mt-1.5" />
                </div>
                <div>
                  <Label className={labelCls}>Email</Label>
                  <Input data-testid="invite-email" type="email" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} className="mt-1.5" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className={labelCls}>Role</Label>
                    <Select value={invite.role} onValueChange={(v) => setInvite({ ...invite, role: v })}>
                      <SelectTrigger data-testid="invite-role" className="mt-1.5"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {ROLE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className={labelCls}>Primary division</Label>
                    <Select value={invite.primary_division} onValueChange={(v) => setInvite({ ...invite, primary_division: v })}>
                      <SelectTrigger data-testid="invite-division" className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        {activeDivNames.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className={labelCls}>Shift</Label>
                  <Select value={invite.shift_name} onValueChange={(v) => setInvite({ ...invite, shift_name: v })}>
                    <SelectTrigger data-testid="invite-shift" className="mt-1.5"><SelectValue placeholder="Assign" /></SelectTrigger>
                    <SelectContent>
                      {shifts.map((s) => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="rounded-md border border-blue-100 bg-blue-50/60 p-3" data-testid="invite-email-notice">
                  <p className="text-xs text-blue-800">The new member will receive a secure email link to set their own password — no temporary password is created or shared.</p>
                </div>
                <p className="text-xs text-slate-500">Fine-tune authorized & supervised divisions after adding, using the member's Edit Member button.</p>
              </div>
              <DialogFooter>
                <Button data-testid="invite-submit" onClick={submitInvite} className="bg-blue-600 hover:bg-blue-700 text-white">Send Invite</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Members */}
        {isAdmin && (
        <>
        <Card className="rounded-md border border-slate-200 bg-white p-6 shadow-sm lg:col-span-2">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-display text-lg font-bold text-slate-900">Members</h2>
            <label className="flex items-center gap-2 text-xs font-semibold text-slate-500">
              <Switch data-testid="show-inactive-toggle" checked={showInactive} onCheckedChange={setShowInactive} />
              Show inactive members
            </label>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-1.5" data-testid="division-filter">
            {["all", ...activeDivNames].map((d) => {
              const selected = divFilter === d;
              return (
                <button
                  key={d}
                  type="button"
                  data-testid={`division-filter-${d}`}
                  onClick={() => setDivFilter(d)}
                  className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${selected ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                >
                  {d === "all" ? "All" : d}
                </button>
              );
            })}
          </div>
          {(() => {
            const inactiveCount = team.filter((m) => m.active === false).length;
            let visible = showInactive ? team : team.filter((m) => m.active !== false);
            if (divFilter !== "all") visible = visible.filter((m) => m.primary_division === divFilter);
            return (
          <div className="mt-3 space-y-2" data-testid="team-list">
            {visible.map((m) => (
              <div key={m.id} data-testid={`member-${m.id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-slate-100 p-3">
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-800 text-xs font-bold text-white">
                    {m.name.split(" ").map((p) => p[0]).slice(0, 2).join("")}
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">{m.name}{m.active === false && <span className="ml-2 rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-slate-500">Inactive</span>}{m.onboarding_pending && <span data-testid={`pending-badge-${m.id}`} className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-700">Pending invite</span>}</p>
                    <p className="text-xs text-slate-500">{m.email} · {m.shift_name || "Unassigned"}{m.pending_shift ? ` → ${m.pending_shift} (scheduled)` : ""}</p>
                    <p className="mt-0.5 text-[11px] text-slate-400" data-testid={`access-summary-${m.id}`}>{accessSummary(m)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <RoleBadge role={m.role} />
                  {isAdmin && m.id !== user.id && m.role !== "system_admin" && m.onboarding_pending && (
                    <Button
                      data-testid={`resend-invite-${m.id}`}
                      variant="outline"
                      size="sm"
                      onClick={() => resendInvite(m)}
                      className="h-8 gap-1 text-amber-700 border-amber-200 hover:bg-amber-50"
                    >
                      <Mail className="h-3.5 w-3.5" /> Resend invite
                    </Button>
                  )}
                  {isAdmin && m.id !== user.id && m.role !== "system_admin" && (
                    <EditMemberDialog member={m} divisions={divisions} shifts={shifts} tz={user?.org_timezone} onSaved={load} />
                  )}
                </div>
              </div>
            ))}
            {visible.length === 0 && <p className="text-sm text-slate-400">No members to show.</p>}
            {!showInactive && inactiveCount > 0 && (
              <p className="pt-1 text-xs text-slate-400" data-testid="inactive-hidden-note">{inactiveCount} inactive member{inactiveCount > 1 ? "s" : ""} hidden.</p>
            )}
          </div>
            );
          })()}
        </Card>

        {/* Shifts */}
        <Card className="rounded-md border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="font-display text-lg font-bold text-slate-900">Shift Names</h2>
          <p className="mt-1 flex items-start gap-1 text-[11px] text-slate-400">
            <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0 text-amber-400" />
            Renaming a shift changes its displayed name for all assigned employees and future records. It does not move employees to another shift.
          </p>
          <div className="mt-3 space-y-2" data-testid="shift-list">
            {shifts.map((s) => (
              <div key={s.id} data-testid={`shift-${s.id}`} className="flex items-center gap-2">
                <Input
                  data-testid={`shift-name-${s.id}`}
                  defaultValue={s.name}
                  disabled={!isAdmin}
                  onBlur={(e) => { const v = e.target.value.trim(); if (v && v !== s.name) renameShift(s.id, v); }}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.target.blur(); } }}
                  className="h-9 text-sm"
                />
                {isAdmin && (
                  <Button data-testid={`shift-delete-${s.id}`} onClick={() => deleteShift(s.id)} variant="outline" size="sm" className="h-9 shrink-0 text-red-600 border-red-200 hover:bg-red-50">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
            {shifts.length === 0 && <p className="text-sm text-slate-400">No shifts yet.</p>}
          </div>
          {isAdmin && (
            <div className="mt-4 flex gap-2">
              <Input data-testid="new-shift-input" value={newShift} onChange={(e) => setNewShift(e.target.value)} placeholder="New shift name"
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addShift(); } }} />
              <Button data-testid="add-shift-button" onClick={addShift} variant="outline" className="gap-1 shrink-0"><Plus className="h-4 w-4" /></Button>
            </div>
          )}
        </Card>

        {/* Divisions */}
        <Card className="rounded-md border border-slate-200 bg-white p-6 shadow-sm" data-testid="division-manager">
          <h2 className="font-display text-lg font-bold text-slate-900">Divisions</h2>
          <div className="mt-3 space-y-2" data-testid="division-list">
            {divisions.map((d) => (
              <div key={d.id} data-testid={`division-${d.id}`} className="flex items-center gap-2">
                <Input
                  data-testid={`division-name-${d.id}`}
                  defaultValue={d.name}
                  disabled={!isAdmin}
                  onBlur={(e) => { const v = e.target.value.trim(); if (v && v !== d.name) renameDivision(d.id, v); }}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.target.blur(); } }}
                  className={`h-9 text-sm ${d.active === false ? "text-slate-400 line-through" : ""}`}
                />
                {isAdmin && (
                  <Button
                    data-testid={`division-toggle-${d.id}`}
                    onClick={() => toggleDivision(d.id, d.active === false)}
                    variant="outline"
                    size="sm"
                    className="h-9 shrink-0 text-xs"
                  >
                    {d.active === false ? "Activate" : "Deactivate"}
                  </Button>
                )}
              </div>
            ))}
            {divisions.length === 0 && <p className="text-sm text-slate-400">No divisions yet.</p>}
          </div>
          {isAdmin && (
            <div className="mt-4 flex gap-2">
              <Input data-testid="new-division-input" value={newDivision} onChange={(e) => setNewDivision(e.target.value)} placeholder="New division"
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addDivision(); } }} />
              <Button data-testid="add-division-button" onClick={addDivision} variant="outline" className="gap-1 shrink-0"><Plus className="h-4 w-4" /></Button>
            </div>
          )}
        </Card>
        </>
        )}
      </div>

      {/* Acknowledgement history */}
      <Card className="rounded-md border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="flex items-center gap-2 font-display text-lg font-bold text-slate-900">
          <Clock className="h-4 w-4 text-slate-500" /> Acknowledgement History
        </h2>
        <div className="mt-3 space-y-2" data-testid="ack-history">
          {ackHistory.length === 0 ? (
            <p className="text-sm text-slate-400">No acknowledgements recorded yet.</p>
          ) : ackHistory.map((a, i) => (
            <div key={i} className="flex items-center justify-between rounded-md border border-slate-100 px-3 py-2">
              <span className="text-sm text-slate-700"><span className="font-semibold">{a.user_name}</span> acknowledged <span className="font-semibold">{a.shift}</span></span>
              <span className="text-xs text-slate-400">{new Date(a.created_at).toLocaleString("en-US", { timeZone: user?.org_timezone || "America/Chicago", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
